/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState, Fragment } from 'react'

import * as Sentry from '@sentry/browser'

import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { routes } from 'helpers/routes'
import { fetchAPI } from 'libs/apis'

import { Checkbox, Button, Tabs } from 'components'
import Modal, { Confirmation } from 'components/Modal'
import { ListTab } from 'components/Tabs'
import { IResponseConsent } from '../../features/Profile/model'

import { ReactComponent as Logo } from 'assets/svg/pdpa-logo.svg'
import { AuthService } from 'features/Auth'
import { useResetRecoilState } from 'recoil'

import { setSessionStorage, currentVersion } from 'helpers/common'

import { BodyProfileType } from '../../features/Profile/model'
import { userProfileState } from 'recoils/atoms'

import { isEmpty } from 'lodash'

const PdpaContainer: React.VFC = () => {
  const { t } = useTranslation(['common', 'error', 'pdpa'])

  const history = useHistory()

  const resetUserProfile = useResetRecoilState(userProfileState)
  const [html, setHtml] = useState('')
  const [isAgree, setIsAgree] = useState(false)
  const [isModalLogOut, setIsModalLogOut] = useState(false)

  // const [isDisabledCheckbox, setIsDisabledCheckbox] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isSelected, setSelection] = useState<any>({
    termsAndConditions: false,
    marketing: false,
    customerPrivacyPolicy: false,
  })
  const [consent, setConsent] = useState<string[]>([])
  const [versionConsent, setVersionConsent] = useState<string>('')

  const base = 'pdpa'

  const [isAcceptConsent, setIsAcceptConsent] = useState<
    BodyProfileType | { applicationId: string }
  >({
    applicationId: 'fixtab-backoffice',
  })

  useEffect(() => {
    getProfile()
  }, [window.location.pathname])

  const getProfile = async () => {
    try {
      const { body: bodyProfile } = await fetchAPI<BodyProfileType>({
        apiUrl: window._env_.REACT_APP_API_URL,
        method: 'GET',
        path: `/v1/user/profile`,
      })

      const _isAcceptConsent = bodyProfile?.consents?.find(
        (profile) => profile.applicationId === 'fixtab-backoffice',
      )

      setIsAcceptConsent(
        _isAcceptConsent as BodyProfileType | { applicationId: string },
      )

      if (isEmpty(_isAcceptConsent)) {
        getTermCon()
      } else {
        history.push(routes.error[404])
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const getTermCon = async () => {
    try {
      const { body }: { body: { text: string; version: string } } =
        await fetchAPI({
          apiUrl: window._env_.REACT_APP_API_URL,
          method: 'GET',
          path: '/v1/pdpa-document',
        })

      setHtml(body.text)
      setVersionConsent(body.version)
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const onChange = (consentText: string) => {
    setSelection({ ...isSelected, [consentText]: !isSelected[consentText] })

    const removeCancelCheck = consent.filter((text) => text !== consentText)

    isSelected[consentText]
      ? setConsent(removeCancelCheck)
      : setConsent([...consent, consentText])
  }

  const onAccept = async () => {
    await setSessionStorage('consents', 'fixtab-backoffice')
    setIsLoadingSubmit(true)

    const payloadConsent = [
      {
        consentTo: consent.join(', '),
        consentVersion: versionConsent,
        applicationId: 'fixtab-backoffice',
      },
    ]

    const requestConsent: any = {
      apiUrl: window._env_.REACT_APP_API_URL,
      method: 'POST',
      path: '/v1/user/consent',
      options: {
        data: payloadConsent,
      },
    }

    if (isAgree) {
      setTimeout(async () => {
        try {
          const response = await fetchAPI<IResponseConsent>(requestConsent)

          if (response.status === 201) {
            setIsLoadingSubmit(false)
            history.push(routes.ticket.path)
          }
        } catch (error) {
          return
        }
      }, 1000)
    }
  }

  const onAgree = () => {
    setIsAgree(!isAgree)
  }

  // Temporary disable this function
  // const handleScroll = (e: any) => {
  //   const scrollToBottom =
  //     Math.floor(e.target.scrollHeight - e.target.scrollTop) - 100 <=
  //     e.target.clientHeight
  //   if (scrollToBottom) {
  //     setIsDisabledCheckbox(false)
  //   } else {
  //     setIsDisabledCheckbox(true)
  //     setIsAgree(false)
  //   }
  // }

  const handleLogout = async () => {
    try {
      resetUserProfile()
      await AuthService.logout()
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const renderPage = (
    html: string,
    isLoading: boolean,
    isAgree: boolean,
    currentVersion: string,
  ) => {
    const PDPA_TABS: ListTab[] = [
      {
        title: t('pdpa:termsAndConditionsOfUse.label'),
        content: <div dangerouslySetInnerHTML={{ __html: html }} />,
        disabled: false,
      },
      {
        title: t('pdpa:protectionOfPersonalInformation.label'),
        content: '',
        disabled: true,
      },
    ]

    return (
      !isAcceptConsent && (
        <Fragment>
          <div className={`${base}__logo`}>
            <Logo />
          </div>

          <Tabs list={PDPA_TABS} isLoading={isLoading} defaultActiveKey={1} />

          <div className={`${base}__footer`}>
            <div className={`${base}__checkbox-layout`}>
              <Checkbox
                className=''
                name='checkbox'
                label='pdpa:acceptTermsAndConditionsOfUse.label'
                onChange={() => {
                  onAgree()
                  onChange('customerPrivacyPolicy')
                }}
                checked={isAgree}
                isLeft
              />
            </div>

            <div className={`${base}__button-layout`}>
              <Button variants='ternary' onClick={() => setIsModalLogOut(true)}>
                {t('common:close.button')}
              </Button>
              <Button
                type='submit'
                isLoading={isLoadingSubmit}
                onClick={onAccept}
                disabled={!isAgree}
              >
                {t('common:accept.button')}
              </Button>
            </div>
          </div>

          <div className={`${base}__version`}>
            {t('common:license.label')} {currentVersion}
          </div>
        </Fragment>
      )
    )
  }

  return (
    <div className={base}>
      {renderPage(html, isLoading, isAgree, currentVersion)}

      <Modal
        isIcon={false}
        isOpen={isModalLogOut}
        onClose={() => setIsModalLogOut(false)}
      >
        <Confirmation
          title={t('common:confirmation.logout.label')}
          description={t('common:confirmation.logout.description.label')}
          confirmText={t('common:confirmation.logout.submit.label')}
          cancelText={t('common:confirmation.logout.cancel.label')}
          onConfirm={() => handleLogout()}
          onClose={() => setIsModalLogOut(false)}
        />
      </Modal>
    </div>
  )
}

export default PdpaContainer
